import React from 'react';
import { ArrowDown, ArrowRight } from '../../../public/icons';

const CollectionItem = ({ 
  collection, 
  onToggleExpand, 
  onSubToggleExpand, 
  onCollectionAction, 
  depth = 0 
}) => {
  const getStatusBadge = (status) => {
    const normalizedStatus = status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase() || 'Draft';
    
    const statusConfig = {
      'Active': {
        textColor: 'text-[#2F8F5B]',
        bgColor: 'bg-[#E7F4ED]',
        dotColor: 'bg-[#2F8F5B]'
      },
      'Draft': {
        textColor: 'text-[#B8860B]',
        bgColor: 'bg-[#FFF9E6]',
        dotColor: 'bg-[#B8860B]'
      },
      'Inactive': {
        textColor: 'text-[#DC2626]',
        bgColor: 'bg-[#FEE2E2]',
        dotColor: 'bg-[#DC2626]'
      },
      'Archived': {
        textColor: 'text-[#6B7280]',
        bgColor: 'bg-[#F3F4F6]',
        dotColor: 'bg-[#6B7280]'
      }
    };

    const config = statusConfig[normalizedStatus] || statusConfig['Draft'];

    return (
      <span className={`flex items-center gap-1.5 px-3 py-1.5 text-xs leading-5 font-semibold rounded-full ${config.bgColor}`}>
        <span className={`h-2 w-2 rounded-full ${config.dotColor}`} />
        <span className={config.textColor}>
          {normalizedStatus}
        </span>
      </span>
    );
  };

  const getCollectionTypeBadge = (type) => {
    const typeConfig = {
      'parent': {
        textColor: 'text-[#2F8F5B]',
        bgColor: 'bg-[#E7F4ED]',
        label: 'Parent'
      },
      'sub': {
        textColor: 'text-[#8F5B2F]',
        bgColor: 'bg-[#F4EDE7]',
        label: 'Sub'
      },
      'standalone': {
        textColor: 'text-[#5B2F8F]',
        bgColor: 'bg-[#EDE7F4]',
        label: 'Standalone'
      }
    };

    const config = typeConfig[type] || typeConfig['standalone'];

    return (
      <span className={`px-3 py-1.5 text-xs leading-5 font-semibold rounded-full ${config.bgColor} ${config.textColor}`}>
        {config.label}
      </span>
    );
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '--';
    return `₦${parseFloat(price).toLocaleString()}`;
  };

  const formatInventory = (collection) => {
    if (collection.collectionType === 'standalone') {
      return `${collection.stockQuantity || 0} in stock`;
    }
    return collection.inventory || '0 Items';
  };

  // ✅ FIXED: Use the hasChildren flag from the collection object
  const hasChildren = collection.hasChildren || 
                     (collection.children && collection.children.length > 0) ||
                     (collection.subCollectionsCount > 0) ||
                     (collection.standaloneCount > 0);
  
  const indentClass = depth > 0 ? `ml-${depth * 6}` : '';

  return (
    <>
      <tr className="hover:bg-gray-50 transition-colors duration-150">
        <td className="px-6 py-4 whitespace-nowrap">
          <div className={`flex items-center gap-3 ${indentClass}`}>
            {hasChildren && (
              <button
                onClick={() => {
                  if (collection.collectionType === 'parent') {
                    onToggleExpand(collection.id, collection.collectionType);
                  } else if (collection.collectionType === 'sub') {
                    onSubToggleExpand(collection.id, collection.collectionType, collection.parentId);
                  }
                }}
                className="text-[#6B635D] hover:text-[#1C2A46] transition-colors"
              >
                {collection.isExpanded ? (
                  <ArrowDown size={16} />
                ) : (
                  <ArrowRight size={16} />
                )}
              </button>
            )}
            {!hasChildren && depth > 0 && <span className="w-6" />}
            <div>
              <div className="text-sm font-medium text-[#0E1628]">{collection.name}</div>
              {collection.description && (
                <div className="text-xs text-[#6B635D] mt-0.5">{collection.description}</div>
              )}
            </div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span className="text-sm font-normal text-[#0E1628]">
            {formatInventory(collection)}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          {getCollectionTypeBadge(collection.collectionType)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span className="text-sm font-normal text-[#0E1628]">
            {formatPrice(collection.price || collection.basePrice)}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          {getStatusBadge(collection.status)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <button
            onClick={() => onCollectionAction(collection.id, 'menu')}
            className="text-[#6B635D] hover:text-[#1C2A46] transition-colors"
          >
            <span className="text-lg font-bold">⋮</span>
          </button>
        </td>
      </tr>
      
      {/* Render children when expanded */}
      {collection.isExpanded && collection.children && collection.children.length > 0 && (
        collection.children.map((child, index) => (
          <CollectionItem
            key={child.id || index}
            collection={child}
            onToggleExpand={onToggleExpand}
            onSubToggleExpand={onSubToggleExpand}
            onCollectionAction={onCollectionAction}
            depth={depth + 1}
          />
        ))
      )}
    </>
  );
};

export default CollectionItem;